import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import type { OfficialDocumentPreset } from "@/lib/api/types";

export const adminPresetKeys = {
  all: ["admin", "official-presets"] as const,
  list: (params?: { search?: string; offset?: number; limit?: number }) =>
    [...adminPresetKeys.all, "list", params] as const,
  detail: (id: number) => [...adminPresetKeys.all, "detail", id] as const,
};

export const adminPresetsQueryOptions = (params: {
  search?: string;
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminPresetKeys.list(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set("search", params.search);
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: OfficialDocumentPreset[];
          total: number;
        }>(`/api/admin/official-presets?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export const adminPresetDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: adminPresetKeys.detail(id),
    queryFn: () =>
      apiClient
        .get<{
          data: OfficialDocumentPreset;
        }>(`/api/admin/official-presets/${id}`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

export function useAdminCreatePreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string;
      imageUrl?: string;
    }) =>
      apiClient.post("/api/admin/official-presets", body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPresetKeys.all });
      toast.success("Preset created");
    },
    onError: () => {
      toast.error("Failed to create preset");
    },
  });
}

export function useAdminUpdatePreset(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      name?: string;
      description?: string;
      imageUrl?: string;
    }) =>
      apiClient
        .patch(`/api/admin/official-presets/${id}`, body)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPresetKeys.all });
    },
  });
}

export function useAdminDeletePreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(`/api/admin/official-presets/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPresetKeys.all });
      toast.success("Preset deleted");
    },
    onError: () => {
      toast.error("Failed to delete preset");
    },
  });
}

export function useAdminAddPresetUrls(presetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urls: Array<{ url: string; label?: string }>) =>
      apiClient
        .post(`/api/admin/official-presets/${presetId}/urls`, { urls })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPresetKeys.all });
      toast.success("URLs added");
    },
    onError: () => {
      toast.error("Failed to add URLs");
    },
  });
}

export function useAdminDiscoverUrls(presetId: number) {
  return useMutation({
    mutationFn: (params: {
      url: string;
      options?: { maxDepth?: number; maxPages?: number };
    }) =>
      apiClient
        .post<{
          data: { jobId: string };
        }>(`/api/admin/official-presets/${presetId}/discover-urls`, params)
        .then((r) => r.data),
  });
}

interface AdminDiscoverJobResponse {
  data: {
    jobId: string;
    status: "pending" | "active" | "completed" | "failed";
    progress?: number;
    message?: string;
    result?: {
      urls?: string[];
    };
    error?: string;
  };
}

export function useAdminDiscoveryJobPolling(jobId: string | null) {
  return useQuery({
    queryKey: ["admin", "discover-job", jobId],
    queryFn: () =>
      apiClient
        .get<AdminDiscoverJobResponse>(
          `/api/admin/official-presets/discover-jobs/${jobId}`
        )
        .then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      return status === "completed" || status === "failed" ? false : 2000;
    },
  });
}

export function useAdminRemovePresetUrl(presetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlId: number) =>
      apiClient
        .delete(`/api/admin/official-presets/${presetId}/urls/${urlId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPresetKeys.all });
    },
    onError: () => {
      toast.error("Failed to remove URL");
    },
  });
}
